/**
 * <ImageCard> — Champ image style Sakinea.
 *
 * Affiche une carte large avec aperçu de l'image, et boutons d'action en overlay
 * en bas (Changer / Cadrer / Supprimer). Inclut l'éditeur de cadrage (point focal
 * + zoom) sous la carte, ouvrable.
 *
 * Props :
 *   - value : string (chemin/URL de l'image actuelle, ex: "/img/x.png" ou "/api/content/asset/xxx")
 *   - fallback : string (placeholder si pas d'image)
 *   - label, hint : optionnels, affichés en bas à gauche de la carte
 *   - aspect : "16/9" | "1/1" | "4/3" (par défaut 16/9)
 *   - onChange(newPath) : appelé quand l'utilisateur modifie l'URL manuellement ou supprime
 *   - onUpload(file) : appelé quand un fichier est sélectionné
 *   - focal : { focal_x, focal_y, zoom } pour le cadrage (optionnel)
 *   - onFocalChange : appelé quand le cadrage change
 */
import { useRef, useState } from "react";
import { Upload, Crop, Trash2 } from "lucide-react";
import { assetUrl } from "@/lib/api";
import FocalPointPicker from "@/components/admin/FocalPointPicker";

export default function ImageCard({
    value,
    fallback,
    label,
    hint,
    aspect = "16/9",
    onChange,
    onUpload,
    focal,
    onFocalChange,
}) {
    const fileRef = useRef(null);
    const [cropOpen, setCropOpen] = useState(false);
    const display = value || fallback;
    const resolved = display ? assetUrl(display) : null;
    const focalX = focal?.focal_x ?? 50;
    const focalY = focal?.focal_y ?? 50;
    const zoom = focal?.zoom ?? 1;

    return (
        <div className="space-y-3">
            {/* Card with image */}
            <div
                className="relative rounded-2xl overflow-hidden border-2 border-border bg-muted group"
                style={{ aspectRatio: aspect.replace("/", " / ") }}
            >
                {resolved ? (
                    <img
                        src={resolved}
                        alt={label || "preview"}
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{
                            objectPosition: `${focalX}% ${focalY}%`,
                            transform: zoom > 1 ? `scale(${zoom})` : undefined,
                            transformOrigin: `${focalX}% ${focalY}%`,
                        }}
                        onError={(e) => { e.target.style.display = "none"; }}
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                        <Upload className="h-8 w-8 mb-2 opacity-50" />
                        <p className="text-sm">Aucune image — clique sur "Changer"</p>
                    </div>
                )}

                {/* Label/hint en bas à gauche */}
                {(label || hint) && resolved && (
                    <div className="absolute bottom-3 left-3 right-3 flex justify-between items-end gap-3 pointer-events-none">
                        <div className="bg-black/60 backdrop-blur rounded-lg px-3 py-2 text-white max-w-[50%]">
                            {label && <p className="text-sm font-bold">{label}</p>}
                            {hint && <p className="text-xs opacity-80">{hint}</p>}
                        </div>
                    </div>
                )}

                {/* Boutons d'action en bas à droite */}
                <div className="absolute bottom-3 right-3 flex gap-2">
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-black/70 hover:bg-black/85 text-white backdrop-blur px-3 py-2 text-xs font-semibold transition-colors"
                        title="Téléverser une nouvelle image"
                    >
                        <Upload className="h-3.5 w-3.5" />
                        Changer
                    </button>
                    {resolved && onFocalChange && (
                        <button
                            type="button"
                            onClick={() => setCropOpen((o) => !o)}
                            className={`inline-flex items-center gap-1.5 rounded-lg backdrop-blur px-3 py-2 text-xs font-semibold transition-colors ${
                                cropOpen
                                    ? "bg-accent text-accent-foreground"
                                    : "bg-black/70 hover:bg-black/85 text-white"
                            }`}
                            title="Régler le cadrage et le zoom"
                        >
                            <Crop className="h-3.5 w-3.5" />
                            Cadrer
                        </button>
                    )}
                    {resolved && (
                        <button
                            type="button"
                            onClick={() => onChange("")}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-black/70 hover:bg-destructive text-white backdrop-blur px-3 py-2 text-xs font-semibold transition-colors"
                            title="Supprimer cette image"
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                            Supprimer
                        </button>
                    )}
                </div>

                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) onUpload(f);
                        // Reset pour pouvoir re-sélectionner le même fichier
                        e.target.value = "";
                    }}
                />
            </div>

            {/* URL input (chemin du fichier, modifiable manuellement) */}
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={fallback || "Chemin de l'image (laisser vide pour aucune)"}
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-xs font-mono"
                />
                {value && (
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="inline-flex items-center justify-center rounded-lg border border-border w-9 h-9 hover:border-destructive hover:text-destructive transition-colors"
                        title="Vider le champ"
                    >
                        ×
                    </button>
                )}
            </div>

            {/* Cadrage panel — ouvrable */}
            {cropOpen && resolved && onFocalChange && (
                <div className="rounded-xl border border-border bg-muted/30 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                            <Crop className="h-3.5 w-3.5" />
                            Cadrage & zoom
                        </p>
                        <button
                            type="button"
                            onClick={() => setCropOpen(false)}
                            className="text-xs text-muted-foreground hover:text-foreground"
                        >
                            Fermer
                        </button>
                    </div>
                    <FocalPointPicker
                        src={resolved}
                        focalX={focalX}
                        focalY={focalY}
                        zoom={zoom}
                        onChange={onFocalChange}
                    />
                </div>
            )}
        </div>
    );
}
